import { useCallback, useEffect, useState } from 'react'
import { api } from '../../../lib/api'
import type { SyncHistory, SyncHistoryDetail } from '../types'

type UseSyncHistoryReturn = {
  history: SyncHistory[]
  loading: boolean
  error: string | null
  total: number
  page: number
  hasMore: boolean
  
  // Actions
  reload: () => Promise<void>
  loadMore: () => Promise<void>
  loadScheduleHistory: (schedulePublicId: string) => Promise<void>
  getDetail: (publicId: string) => Promise<SyncHistoryDetail | null>
  
  // State
  selectedScheduleId: string | null
}

const PER_PAGE = 20

/**
 * Hook pour gérer l'historique des synchronisations WordPress
 * Responsabilité unique : Lecture historique
 */
export function useSyncHistory(): UseSyncHistoryReturn {
  const [history, setHistory] = useState<SyncHistory[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  const [selectedScheduleId, setSelectedScheduleId] = useState<string | null>(null)

  const fetchHistory = useCallback(async (
    scheduleId: string | null,
    pageNum: number,
    append: boolean = false
  ) => {
    setLoading(true)
    setError(null)
    try {
      const endpoint = scheduleId
        ? `/wp/schedules/${scheduleId}/history`
        : '/wp/history'
      
      const { data } = await api.get(endpoint, {
        params: { page: pageNum, per_page: PER_PAGE }
      })
      
      if (append) {
        setHistory(prev => [...prev, ...(data.items || [])])
      } else {
        setHistory(data.items || [])
      }
      setTotal(data.total || 0)
      setHasMore(data.has_more || false)
      setPage(pageNum)
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Erreur chargement historique')
    } finally {
      setLoading(false)
    }
  }, [])

  const reload = useCallback(async () => {
    setPage(1)
    await fetchHistory(selectedScheduleId, 1, false)
  }, [fetchHistory, selectedScheduleId])

  const loadMore = useCallback(async () => {
    if (loading || !hasMore) return
    await fetchHistory(selectedScheduleId, page + 1, true)
  }, [fetchHistory, selectedScheduleId, page, loading, hasMore])

  const loadScheduleHistory = useCallback(async (schedulePublicId: string) => {
    setSelectedScheduleId(schedulePublicId)
    setPage(1)
    await fetchHistory(schedulePublicId, 1, false)
  }, [fetchHistory])

  const getDetail = useCallback(async (publicId: string): Promise<SyncHistoryDetail | null> => {
    try {
      const { data } = await api.get(`/wp/history/${publicId}`)
      return data
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Erreur chargement détail')
      return null
    }
  }, [])

  // Chargement initial (historique global)
  useEffect(() => {
    fetchHistory(null, 1, false)
  }, [fetchHistory])

  return {
    history,
    loading,
    error,
    total,
    page,
    hasMore,
    reload,
    loadMore,
    loadScheduleHistory,
    getDetail,
    selectedScheduleId,
  }
}

export default useSyncHistory
